import React, { useEffect, useMemo, useState } from 'react';
import ErrorComponent from '@components/error/ErrorComponent';
import Loading from '@components/loading/Loading';
import { Player } from '@beatport/ui/dist/cjs/Player';
import { Button } from '@beatport/ui/dist/cjs/Buttons';
import ErrorBoundary from '@components/error/ErrorBoundary';
import './App.scss';
import useCatalog from 'hooks/useCatalog';
import { useQueryParam, NumberParam, StringParam } from 'use-query-params';
import IconShareAlt from '@components/icons/ShareAlt';
import IconShoppingCart from '@components/icons/ShoppingCart';
import { makeBeatportUrl } from '@utils/urls';
import { Modal } from '@beatport/ui/dist/cjs/Modal';
import IconFacebook from '@components/icons/Facebook';
import IconTwitter from '@components/icons/Twitter';

const player = React.createRef();
window.BeatportEmbed = window.BeatportEmbed || {}
window.BeatportEmbed.player = player;


import { initBroadcastChannel, INSTANCE_ID } from '@utils/broadcastChannel';
import BeatportLogo from '@components/icons/BeatportLogo';

let channel;
try {
  // Currently this fails in Safari when embedded in an iframe
  // https://github.com/pubkey/broadcast-channel/issues/419
  //
  channel = initBroadcastChannel();
} catch (e) {
  console.warn(e);
}

const ALLOWED_TYPES = ['track'];

const App: React.FC = () => {
  const [id] = useQueryParam('id', NumberParam);
  const [_artwork] = useQueryParam('artwork', NumberParam);
  const [_type] = useQueryParam('type', StringParam);
  const type = useMemo(() => !_type ? 'track' : _type, []);
  const artwork = useMemo(() => !_artwork ? 0 : _artwork, []);
  const { response, loading: catalogLoading, error: catalogError } = useCatalog(type, id);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  useEffect(() => {
    window.BeatportEmbed?.player?.current
  }, [window.BeatportEmbed?.player?.current]);

  if (catalogLoading) {
    return (
      <Loading />
    )
  }

  if (catalogError) {
    return <ErrorComponent title={catalogError.message} />
  }


  if (!id) {
    return <ErrorComponent title={`Sorry, we couldn't find that ${type}.`} />
  }

  if (ALLOWED_TYPES.indexOf(type) === -1) {
    return <ErrorComponent title={`Sorry, that item can't be displayed.`} />
  }

  return (
    <ErrorBoundary>
      <div className="App">
        <header className="App__header">
          <BeatportLogo className="App__header-logo" />
        </header>
        <div className="App__player-container">
          <Player
            ref={player}
            deferLoadMedia={true}
            PlayerArtwork={({ playlist }) => {
              if (artwork === 0 && playlist.current) {
                return (
                  <span className="Player__artwork-2">
                    <img
                      alt={playlist.current.name}
                      key={playlist.current.uiuid}
                      id={playlist.current.uiuid}
                      src={playlist?.current?.release?.image?.uri}
                    />
                  </span>
                )
              }

              return null;
            }}
            onPlayStateChange={(status) => {
              if (channel) {
                channel.postMessage(JSON.stringify({ code: 'playstatechange', status, instance_id: INSTANCE_ID }))
              }
            }}
            renderPlayerExtra={(state) => {
              const current = state?.playlist?.current;

              if (!current) {
                return;
              }

              if (current.type === 'track' && current.id) {
                let title = current.name;
                if (current.mix_name) {
                  title += ` (${current.mix_name})`;
                }

                const shareTitle = `Check out ${title} on Beatport`

                const onBuyClick = () => window.open(makeBeatportUrl(current), '_blank');
                const onShareClick = () => setShareModalOpen(true);


                const facebookShareUrl = `http://www.facebook.com/sharer/sharer.php?u=${makeBeatportUrl(current)}`;
                const twitterShareUrl = `http://twitter.com/intent/tweet?&text=${shareTitle}&url=${makeBeatportUrl(current)}`

                return (
                  <>
                    <Button className="ActionButton ActionButton--buy" category="option" nomin onClick={onBuyClick}>
                      <IconShoppingCart className="ActionButton__icon" />
                      <span className="ActionButton__text">Buy on Beatport</span>
                    </Button>
                    <Button className="ActionButton ActionButton--share" category="option" nomin onClick={onShareClick}>
                      <IconShareAlt className="ActionButton__icon" />
                      <span className="ActionButton__text">Share</span>
                    </Button>
                    <Modal
                      open={shareModalOpen}
                      onClose={() => setShareModalOpen(false)}
                      showCloseButton
                    >
                      <div className="Share">
                        <Button
                          className="ShareButton"
                          size="large"
                          fullWidth
                          onClick={() => window.open(facebookShareUrl, '_blank')}
                        >
                          <IconFacebook className="ShareButton__icon" />
                          <span className="ShareButton__text">Share on Facebook</span>
                        </Button>
                      </div>
                      <div>
                        <Button
                          className="ShareButton"
                          size="large"
                          fullWidth
                          onClick={() => window.open(twitterShareUrl, '_blank')}
                        >
                          <IconTwitter className="ShareButton__icon" />
                          <span className="ShareButton__text">Share on Twitter</span>
                        </Button>
                      </div>
                    </Modal>
                  </>
                )
              }

            }}
          />
        </div>
        {
          artwork === 1 && (
            <div className="App__info-container">
              {
                (type === 'track' && response?.id) && (
                  <div style={{
                    padding: '10px',
                    width: '100%',
                    height: '100%',
                  }}>
                    <div style={{
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${response.release?.image?.uri})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'top center',
                    }} />
                  </div>
                )
              }
            </div>
          )
        }

      </div>

    </ErrorBoundary>
  );
}

export default App;
